import React, { useRef } from 'react';
import { Plus, X, ImagePlus, Loader2 } from 'lucide-react';
import axios, { API } from '../../utils/api';
import { PHOTO_ACCEPT, HEIC_MESSAGE, isHeic } from '../../utils/imageFile';

const BORDER = '#E5E7EB';
const MUTED = '#6B7280';
const ERR = '#B91C1C';
const POLL_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_RAW_BYTES = 10 * 1024 * 1024;

export const resolvePollImg = (url) => (url && url.startsWith('/api') ? `${API.replace(/\/api$/, '')}${url}` : url);

export const emptyPollOption = () => ({ text: '', image_url: null });

const OptionPhoto = ({ opt, onPick, onRemove, index }) => {
  const inputRef = useRef(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
      {opt.image_url && (
        <img src={resolvePollImg(opt.image_url)} alt={opt.text || `Option ${index + 1}`} data-testid={`poll-option-photo-preview-${index}`}
          style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 6, border: `1px solid ${BORDER}` }} />
      )}
      <input ref={inputRef} type="file" accept={PHOTO_ACCEPT} style={{ display: 'none' }}
        data-testid={`poll-option-photo-input-${index}`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={opt.uploading}
        data-testid={`poll-option-add-photo-${index}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, background: '#FFF', color: '#374151', fontSize: 12, cursor: opt.uploading ? 'wait' : 'pointer' }}>
        {opt.uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
        {opt.uploading ? 'Uploading…' : opt.image_url ? 'Change photo' : 'Add photo'}
      </button>
      {opt.image_url && !opt.uploading && (
        <button type="button" onClick={onRemove} data-testid={`poll-option-remove-photo-${index}`}
          style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, background: '#FFF', color: MUTED, fontSize: 12, cursor: 'pointer' }}>
          Remove photo
        </button>
      )}
      {opt.error && <div data-testid={`poll-option-photo-error-${index}`} style={{ width: '100%', fontSize: 12, color: ERR }}>{opt.error}</div>}
    </div>
  );
};

export const PollOptionsEditor = ({ options, setOptions }) => {
  const patch = (i, p) => setOptions(prev => prev.map((o, idx) => (idx === i ? { ...o, ...p } : o)));

  const upload = async (i, file) => {
    if (isHeic(file)) { patch(i, { error: HEIC_MESSAGE }); return; }
    if (!POLL_PHOTO_TYPES.includes(file.type)) { patch(i, { error: 'Only JPEG, PNG or WebP photos are allowed.' }); return; }
    if (file.size > MAX_RAW_BYTES) { patch(i, { error: 'Photo must be under 10 MB.' }); return; }
    patch(i, { uploading: true, error: null });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload?purpose=poll_photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      patch(i, { uploading: false, image_url: data.url });
    } catch (e) {
      const d = e.response?.data?.detail;
      patch(i, { uploading: false, error: typeof d === 'string' ? d : 'Photo upload failed. Please try again.' });
    }
  };

  return (
    <div>
      {options.map((opt, index) => (
        <div key={opt.id || `new-${index}`} data-testid={`poll-option-row-${index}`}
          style={{ marginBottom: 10, padding: 10, border: `1px solid ${BORDER}`, borderRadius: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={opt.text} maxLength={120} onChange={(e) => patch(index, { text: e.target.value })}
              placeholder={`Option ${index + 1}`} data-testid={`poll-option-text-${index}`}
              style={{ flex: 1, minWidth: 0, padding: 10, borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14 }} />
            {options.length > 2 && (
              <button type="button" onClick={() => setOptions(prev => prev.filter((_, i) => i !== index))} aria-label="Remove option"
                data-testid={`poll-option-remove-${index}`}
                style={{ padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, background: '#FFF', cursor: 'pointer' }}>
                <X size={16} color={MUTED} />
              </button>
            )}
          </div>
          <OptionPhoto opt={opt} index={index} onPick={(f) => upload(index, f)} onRemove={() => patch(index, { image_url: null, error: null })} />
        </div>
      ))}
      <button type="button" onClick={() => setOptions(prev => [...prev, emptyPollOption()])} data-testid="poll-add-option"
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 6, border: `1px dashed ${BORDER}`, background: 'none', color: MUTED, fontSize: 13, cursor: 'pointer' }}>
        <Plus size={14} /> Add Option
      </button>
    </div>
  );
};

export const pollOptionsPayload = (options) => options
  .filter(o => o.text.trim())
  .map(o => ({ ...(o.id ? { id: o.id } : {}), text: o.text.trim(), image_url: o.image_url || null }));

export const pollOptionsBusy = (options) => options.some(o => o.uploading);

export const PollClosingTime = ({ value, onChange, disabled }) => (
  <div style={{ marginTop: 10 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Closing Time (optional, India time)</label>
    <input type="time" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid="poll-closing-time"
      style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, boxSizing: 'border-box', background: disabled ? '#F9FAFB' : '#FFF' }} />
    <p style={{ margin: '6px 0 0', fontSize: 12, color: MUTED }}>
      {disabled ? 'Pick a closing date first.' : 'Without a time, voting closes at the end of the closing date (India time).'}
    </p>
  </div>
);
